/**
 * The information architecture of school settings, in one place.
 *
 * The rail, the overview ledger, and the router all read from this list, so
 * adding a section means adding one entry here rather than editing three files
 * that can drift apart.
 *
 * `blurb` is written from the administrator's side of the desk: it says where
 * in the product the setting actually shows up, because "Grade scale" alone
 * does not tell you that it is what turns a percentage into a letter on a
 * report card.
 */

export type SettingsGroupKey = 'school' | 'grading' | 'money' | 'families' | 'account';

export interface SettingsSection {
  /** URL segment under /app/:schoolSlug/settings, and the legacy ?tab= value. */
  slug: string;
  /** Rail and overview label. */
  label: string;
  group: SettingsGroupKey;
  /** One line on what this governs and where it surfaces. */
  blurb: string;
  /**
   * School configuration, which only administrators get. Teachers and parents
   * open the same page and see their account and nothing else: how the school
   * is set up is not their concern, and showing it read-only would still be
   * the SIS leaking into views that are not about running the school.
   */
  adminOnly?: boolean;
  /**
   * Whether this section reads the page's school-settings query. Rooms and
   * grading templates load their own data, and the account section loads none,
   * so making them wait on a query they never read would be a blank screen for
   * no reason.
   */
  usesSettingsQuery?: boolean;
}

export interface SettingsGroup {
  key: SettingsGroupKey;
  label: string;
}

/** Rail order. Setup you do first comes first. */
export const SETTINGS_GROUPS: readonly SettingsGroup[] = [
  { key: 'school', label: 'Your school' },
  { key: 'grading', label: 'Grading' },
  { key: 'money', label: 'Tuition & payments' },
  { key: 'families', label: 'Families' },
  // Unlabelled: this group holds one section, already called "Your account", and
  // a heading repeating its only item is a heading saying nothing.
  { key: 'account', label: '' },
];

export const SETTINGS_SECTIONS: readonly SettingsSection[] = [
  {
    slug: 'school-years',
    label: 'School years & terms',
    group: 'school',
    blurb: 'The years and terms every course, class, and gradebook is filed under.',
    adminOnly: true,
  },
  {
    slug: 'grade-levels',
    label: 'Grade levels',
    group: 'school',
    blurb: 'The grades a student can be enrolled into, in the order you list them.',
    adminOnly: true,
    usesSettingsQuery: true,
  },
  {
    slug: 'classrooms',
    label: 'Rooms & locations',
    group: 'school',
    blurb: 'The places a class meeting can be assigned to on the schedule.',
    adminOnly: true,
  },
  {
    slug: 'grade-scale',
    label: 'Grade scale',
    group: 'grading',
    blurb: 'How a percentage becomes a letter and GPA points on a report card.',
    adminOnly: true,
    usesSettingsQuery: true,
  },
  {
    slug: 'grading-templates',
    label: 'Grading templates',
    group: 'grading',
    blurb: 'Reusable category sets a teacher can pick to start a class gradebook.',
    adminOnly: true,
  },
  {
    slug: 'tuition-and-fees',
    label: 'Tuition & fees',
    group: 'money',
    blurb: 'What a family is charged for the year, and how they are allowed to pay it.',
    adminOnly: true,
    usesSettingsQuery: true,
  },
  {
    slug: 'payments',
    label: 'Getting paid',
    group: 'money',
    blurb: 'The Stripe account that collects tuition from families and pays it to the school.',
    adminOnly: true,
    usesSettingsQuery: true,
  },
  {
    slug: 'enrollment-checklists',
    label: 'Enrollment checklists',
    group: 'families',
    blurb: 'The steps a family completes to re-enroll or apply, and the rules that decide when they are enrolled.',
    adminOnly: true,
  },
  {
    slug: 'parent-alerts',
    label: 'Parent dashboard',
    group: 'families',
    blurb: 'Whether families can sign in at all, and what their dashboard raises when they do.',
    adminOnly: true,
    usesSettingsQuery: true,
  },
  {
    slug: 'account',
    label: 'Your account',
    group: 'account',
    blurb: 'Your sign-in, the school you are working in, and how your sidebar is arranged.',
  },
];

const BY_SLUG = new Map(SETTINGS_SECTIONS.map((section) => [section.slug, section]));

/** Sections that were folded into another; an old link opens the one they went to. */
const MOVED_SLUGS: Readonly<Record<string, string>> = {
  'enrollment-prices': 'tuition-and-fees',
};

export function settingsSection(slug: string | undefined): SettingsSection | null {
  return slug ? BY_SLUG.get(MOVED_SLUGS[slug] ?? slug) ?? null : null;
}

export function visibleSections(isAdmin: boolean): SettingsSection[] {
  return SETTINGS_SECTIONS.filter((section) => isAdmin || !section.adminOnly);
}

export function sectionsInGroup(group: SettingsGroupKey, isAdmin = true): SettingsSection[] {
  return visibleSections(isAdmin).filter((section) => section.group === group);
}

export function visibleGroups(isAdmin: boolean): SettingsGroup[] {
  return SETTINGS_GROUPS.filter((group) => sectionsInGroup(group.key, isAdmin).length > 0);
}

export function settingsPath(schoolSlug: string, slug?: string): string {
  const base = `/app/${encodeURIComponent(schoolSlug)}/settings`;
  return slug ? `${base}/${slug}` : base;
}
